/**
 * @file gerenciarEmails.js
 * @brief Componente de React para a funcionalidade de Gestão de Destinatários de E-mail
 * Este componente permite que usuários administradores 
 * gerenciem quais usuários de suas próprias empresas receberão quais tipos de
 * e-mails transacionais enviados pelo sistema (ex: "Notificação de Pagamento").
 * 
 * Utiliza uma interface de "Dual Listbox" (Disponíveis vs. Selecionados) com
 * suporte a drag-and-drop 
 * 
 * @date 2025-10-21
 */

import React, { useState, useEffect, useMemo } from "react";
import axios from 'axios';
import styled from "styled-components";
import GerenciarEmailsLayout from "./gerenciarEmailsLayout";
import UserListBox from "./userListBox";
import { useNavigate } from "react-router-dom";

// --- INÍCIO DOS STYLED COMPONENTS ---

const PageSection = styled.section`
    margin-top: 25px;
`;

const Header = styled.div`
    padding: 0.25rem;
    margin-bottom: 1.5rem;
`;

const H2 = styled.h2`
    font-size: 1.5rem;
    font-weight: bold;
    color: #2d3748;
`;

const P = styled.p`
    color: #718096;
    margin-top: 0.25rem;
`;

const MainLayout = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    @media (min-width: 1024px) {
        flex-direction: row;
    }
`;

const Sidebar = styled.aside`
    @media (min-width: 1024px) {
        width: 33.333333%;
    }
    @media (min-width: 1280px) {
        width: 25%;
    }
`;

const MainContent = styled.main`
    @media (min-width: 1024px) {
        width: 66.666667%;
    }
    @media (min-width: 1280px) {
        width: 75%;
    }
`;

const Card = styled.div`
    background-color: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
`;

const CardHeader = styled.div`
    padding: 1.25rem;
    border-bottom: 1px solid #e2e8f0;
`;

const CardTitle = styled.h3`
    font-size: 1.125rem;
    font-weight: bold;
    color: #2d3748;
`;

const CardDescription = styled.p`
    color: #718096;
    font-size: 0.875rem;
    margin-top: 0.25rem;
`;

const CardBody = styled.div`
    padding: 1.25rem;
`;

const CardFooter = styled.div`
    padding: 1.25rem;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: flex-end;
`;

const EmailTypeList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const EmailTypeButton = styled.button`
    width: 100%;
    text-align: left;
    padding: 0.625rem 0.75rem;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    transition: all 0.2s;
    border: none;
    cursor: pointer;
    background-color: ${props => props.active ? '#14a2b8' : 'transparent'};
    color: ${props => props.active ? '#fff' : '#374151'};
    box-shadow: ${props => props.active ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none'};

    &:hover {
        background-color: ${props => props.active ? '#178495ff' : '#F3F4F6'};
    }
`;

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
    align-items: flex-start;
    @media (min-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const SaveButton = styled.button`
    background-color: #14a2b8;
    color: white;
    font-weight: bold;
    padding: 0.5rem 1.5rem;
    border-radius: 0.5rem;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 120px;
    height: 38px;

    &:hover {
        background-color: #178495ff;
    }

    &:focus {
        outline: 2px solid transparent;
        outline-offset: 2px;
        box-shadow: 0 0 0 2px #fff, 0 0 0 4px #19bed7ff;
    }
    
    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }
`;
// --- FIM DOS STYLED COMPONENTS ---

// --- DADOS SIMULADOS (MOCK) ---
/**
 * @constant mockEmailTypes
 * @brief Lista estática dos tipos de e-mail que o sistema pode enviar
 */
const mockEmailTypes = [
    {
        id: 1,
        name: "Confirmação de Workflow",
        description:
            "Enviado para os usuários selecionados sempre que um novo workflow de processo é confirmado no sistema.",
    },
    {
        id: 2,
        name: "Notificação de Prazo",
        description:
            "Alerta sobre prazos importantes que estão se aproximando ou que foram recém-criados para um processo.",
    },
    {
        id: 3,
        name: "Envio de Anexo",
        description:
            "Notifica os usuários quando um novo anexo é adicionado a um processo que eles seguem.",
    },
    {
        id: 4,
        name: "Envio de Documento",
        description:
            "Similar ao anexo, mas específico para documentos oficiais que foram carregados no portal.",
    },
    {
        id: 5,
        name: "Notificação de Pagamento",
        description:
            "Informa sobre um novo pagamento registrado no sistema, incluindo detalhes do valor e data.",
    },
    {
        id: 6,
        name: "Envio de Recibo",
        description:
            "Enviado para confirmar o recebimento de pagamentos, com o recibo correspondente em anexo.",
    },
];

// --- COMPONENTES AUXILIARES ---
/**
 * @component CheckIcon
 * @brief Ícone de "sucesso" exibido no botão Salvar
 */
const CheckIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        height="24"
        width="24"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
    >
        {" "}
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
        />{" "}
    </svg>
);

/**
 * @component LoadingSpinner
 * @brief Ícone de "carregando" para feedback de carregamento de dados ou salvamento
 */
const LoadingSpinner = () => (
    <div
        style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
        }}
    >
        {" "}
        <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "24px", height: "24px", color: "white" }}
        >
            {" "}
            <span className="sr-only">Loading...</span>{" "}
        </div>{" "}
    </div>
);

// --- COMPONENTE PRINCIPAL ---
/**
 * @component GestaoEmails
 * @brief Componente principal para a página de gerenciamento de destinatários
 * * Orquestra o estado, busca de dados (simulada) e a renderização da
 * interface de gerenciamento.
 */
function GestaoEmails() {
    const navigate = useNavigate();
    
    // --- ESTADOS DE AUTENTICAÇÃO E USUÁRIO ---
    const [authenticated, setAuthenticated] = useState(false);
    const [user, setUser] = useState(null);

    // --- ESTADOS DE UI (User Interface) ---
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    // --- ESTADOS DE DADOS ---
    const [emailTypes, setEmailTypes] = useState([]);
    const [clientUsers, setClientUsers] = useState([]);
    const [selections, setSelections] = useState({});
    const [activeEmailTypeId, setActiveEmailTypeId] = useState(null);

    useEffect(() => {
        axios
            .post("https://guias.almeidaenogueira.com.br/api/get/email-recipients-data", {
                withCredentials: true,
            })
            .then((response) => {
                if (response.data.auth === true) {
                    setAuthenticated(true);
                    setUser(response.data.user);
                    setClientUsers(response.data.clientUsers);
                    setSelections(response.data.selections);

                        // Mantém os tipos de e-mail do frontend
                        setEmailTypes(mockEmailTypes);
                        // Define o primeiro e-mail como ativo por padrão
                        if (mockEmailTypes.length > 0) {
                            setActiveEmailTypeId(mockEmailTypes[0].id);
                        }

                     setLoading(false); // Desliga o spinner de carregamento
                } else {
                    setLoading(false);
                    navigate("/login");
                }
            })
            .catch((err) => {
                // Em caso de erro de rede ou 500
                console.error("Erro ao buscar dados:", err);
                setLoading(false);
                navigate("/login");
            });
    }, [navigate]);

    /**
     * @brief Calcula as listas de usuários "disponíveis" e "selecionados" usando `useMemo`
     * * Este hook otimiza a performance, recalculando as listas apenas quando
     * os dados (`clientUsers`, `selections`) ou o tipo de e-mail ativo mudam
     * @returns {Object} Um objeto contendo `{ availableUsers, selectedUsers }`.
     */
    const { availableUsers, selectedUsers } = useMemo(() => {
        if (!activeEmailTypeId || clientUsers.length === 0)
            return { availableUsers: [], selectedUsers: [] };

        // Cria um Set (para performance) com os IDs dos usuários já selecionados
        const selectedIds = new Set(selections[activeEmailTypeId] || []);

        // Filtra a lista total de usuários do cliente
        const available = clientUsers.filter(
            (user) => !selectedIds.has(user.id) // Disponíveis = NÃO estão no Set
        );
        const selected = clientUsers.filter((user) => selectedIds.has(user.id)); // Selecionados = ESTÃO no Set

        return { availableUsers: available, selectedUsers: selected };
    }, [clientUsers, selections, activeEmailTypeId]);

    /**
     * @function handleMove
     * @brief Função central que atualiza o estado `selections`
     * * É chamada pelos botões de seta e pelo drag-and-drop
     * @param {Array<number>} userIdsToMove - Array de IDs de usuários a serem movidos
     * @param {"toSelected" | "toAvailable"} direction - A direção do movimento
     */
    const handleMove = (userIdsToMove, direction) => {
        const currentSelectedIds = selections[activeEmailTypeId] || [];
        let newSelectedIds;

        if (direction === "toSelected") {
            // Adiciona os novos IDs à lista de selecionados, usando Set para evitar duplicatas
            newSelectedIds = [
                ...new Set([...currentSelectedIds, ...userIdsToMove]),
            ];
        } else {
            // Remove os IDs da lista de selecionados
            const idsToRemove = new Set(userIdsToMove);
            newSelectedIds = currentSelectedIds.filter(
                (id) => !idsToRemove.has(id)
            );
        }

        // Atualiza o estado `selections` de forma imutável
        setSelections((prev) => ({
            ...prev,
            [activeEmailTypeId]: newSelectedIds,
        }));
    };

    // Fução handleSave (real)
    const handleSave = async () => {
        setSaving(true);
        
        try {
            // Envia o objeto 'selections' completo para o backend
            const response = await axios.post(
                "https://guias.almeidaenogueira.com.br/api/update/email-recipients", 
                { selections: selections }, // Envia { "selections": { ... } } no corpo da requisição
                { withCredentials: true }
            );

            if (response.data.auth && response.data.success) {
                // Sucesso! Ativa o feedback visual
                setShowSuccess(true);
                setTimeout(() => setShowSuccess(false), 2000);
            } else {
                // Falha na autenticação ou no salvamento
                alert("Falha ao salvar. Você pode precisar fazer login novamente.");
                navigate("/login");
            }

        } catch (error) {
            console.error("Erro ao salvar:", error);
            alert("Ocorreu um erro de rede ao tentar salvar as alterações.");
        } finally {
            // Garante que o estado 'saving' seja desativado
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <LoadingSpinner />
            </div>
        );
    }

    // --- BLOQUEIO DE AUTENTICAÇÃO ---
    // Roda um spinner enquanto autentica
    if (!authenticated) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    // Redireciona usuários não-administradores
    if (user && user.id !== 1) {
        navigate("/bad/request");
        return null;
    }

    // --- RENDERIZAÇÃO DO COMPONENTE ---
    return (
        <GerenciarEmailsLayout>
            <PageSection>
                <Header>
                    <H2>Gestão de Destinatários de E-mail</H2>
                    <P>
                        Selecione um tipo de notificação e configure quais
                        usuários da sua equipe devem recebê-la.
                    </P>
                </Header>
                <MainLayout>
                    <Sidebar>
                        <Card>
                            <CardBody>
                                <h3
                                    style={{
                                        fontWeight: 600,
                                        color: "#4A5568",
                                        marginBottom: "0.75rem",
                                        padding: "0 0.5rem",
                                    }}
                                >
                                    Tipos de Notificação
                                </h3>
                                <EmailTypeList>
                                    {emailTypes.map((type) => (
                                        <li key={type.id}>
                                            <EmailTypeButton
                                                onClick={() =>
                                                    setActiveEmailTypeId(
                                                        type.id
                                                    )
                                                }
                                                active={
                                                    activeEmailTypeId ===
                                                    type.id
                                                }
                                            >
                                                {type.name}
                                            </EmailTypeButton>
                                        </li>
                                    ))}
                                </EmailTypeList>
                            </CardBody>
                        </Card>
                    </Sidebar>
                    <MainContent>
                        <Card>
                            {activeEmailTypeId ? (
                                <>
                                    <CardHeader>
                                        <CardTitle>
                                            {
                                                emailTypes.find(
                                                    (t) =>
                                                        t.id ===
                                                        activeEmailTypeId
                                                )?.name
                                            }
                                        </CardTitle>
                                        <CardDescription>
                                            {
                                                emailTypes.find(
                                                    (t) =>
                                                        t.id ===
                                                        activeEmailTypeId
                                                )?.description
                                            }
                                        </CardDescription>
                                    </CardHeader>
                                    <CardBody>
                                        <GridContainer>
                                            <UserListBox
                                                title="Usuários Disponíveis"
                                                users={availableUsers}
                                                onMove={(ids) =>
                                                    handleMove(
                                                        ids,
                                                        "toSelected"
                                                    )
                                                }
                                                onMoveAll={() =>
                                                    handleMove(
                                                        availableUsers.map(
                                                            (u) => u.id
                                                        ),
                                                        "toSelected"
                                                    )
                                                }
                                                onDrop={(userId) =>
                                                    handleMove(
                                                        [userId],
                                                        "toAvailable"
                                                    )
                                                } // CORREÇÃO AQUI
                                            />
                                            <UserListBox
                                                title="Destinatários Selecionados"
                                                users={selectedUsers}
                                                onMove={(ids) =>
                                                    handleMove(
                                                        ids,
                                                        "toAvailable"
                                                    )
                                                }
                                                onMoveAll={() =>
                                                    handleMove(
                                                        selectedUsers.map(
                                                            (u) => u.id
                                                        ),
                                                        "toAvailable"
                                                    )
                                                }
                                                onDrop={(userId) =>
                                                    handleMove(
                                                        [userId],
                                                        "toSelected"
                                                    )
                                                } // CORREÇÃO AQUI
                                                isDestination={true}
                                            />
                                        </GridContainer>
                                    </CardBody>
                                    <CardFooter>
                                        <SaveButton
                                            onClick={handleSave}
                                            disabled={saving || showSuccess}
                                        >
                                            {saving ? (
                                                <LoadingSpinner />
                                            ) : showSuccess ? (
                                                <CheckIcon />
                                            ) : (
                                                "Salvar"
                                            )}
                                        </SaveButton>
                                    </CardFooter>
                                </>
                            ) : (
                                <CardBody
                                    style={{
                                        textAlign: "center",
                                        color: "#A0AEC0",
                                    }}
                                >
                                    <p>
                                        Selecione um tipo de notificação para
                                        começar.
                                    </p>
                                </CardBody>
                            )}
                        </Card>
                    </MainContent>
                </MainLayout>
            </PageSection>
        </GerenciarEmailsLayout>
    );
}


export default GestaoEmails;
